import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, IconButton, InputLabel, ListItemText, MenuItem,
  OutlinedInput, Paper, Radio, RadioGroup, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { API, getAuthHeader, fmtDate } from './customerApp/shared';
import { useOrg } from '../context/OrgContext';

const BASE = `${API}/api/notifications-admin`;
const errorText = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

/* ------------------------------------------------------------------------ */
/* Switchboard                                                               */
/* ------------------------------------------------------------------------ */

function RoleFilter({ value, options, onChange, label }) {
  return (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (selected.length ? selected.map((k) => options.find((o) => String(o.key) === String(k))?.label || k).join(', ') : 'Everyone')}
        displayEmpty
      >
        {options.map((o) => (
          <MenuItem key={o.key} value={o.key}>
            <Checkbox size="small" checked={value.some((v) => String(v) === String(o.key))} />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function Switchboard() {
  const [data, setData] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await axios.get(`${BASE}/policy`, { headers: getAuthHeader() });
      setData(res);
      setPolicy(res.policy);
      setSaved(JSON.stringify(res.policy));
    } catch (e) {
      setError(errorText(e, 'Could not load notification settings'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = policy && JSON.stringify(policy) !== saved;

  const setMaster = (channel, enabled) => setPolicy((p) => ({ ...p, channels: { ...p.channels, [channel]: { enabled } } }));
  const setEvent = (key, patch) => setPolicy((p) => ({ ...p, events: { ...p.events, [key]: { ...p.events[key], ...patch } } }));
  const setEventChannel = (key, channel, on) => setPolicy((p) => ({
    ...p, events: { ...p.events, [key]: { ...p.events[key], channels: { ...p.events[key].channels, [channel]: on } } }
  }));

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { data: res } = await axios.put(`${BASE}/policy`, policy, { headers: getAuthHeader() });
      setPolicy(res.policy);
      setSaved(JSON.stringify(res.policy));
      setNotice('Saved. Messages sent from now on follow these switches.');
    } catch (e) {
      setError(errorText(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!policy || !data) return <Alert severity="error">{error || 'Could not load notification settings'}</Alert>;

  const { catalog } = data;
  const offCritical = catalog.events.filter((e) => e.critical && (!policy.events[e.key].enabled || !Object.values(policy.events[e.key].channels).some(Boolean)));

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
      {offCritical.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />}>
          Switched off: {offCritical.map((e) => e.label).join(', ')}. Drivers and providers are not told about offers, or applicants hear nothing — jobs and hires are missed while this stays off.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Master switches</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Off here stops that channel for every automated message below. Sign-in codes, password resets and new-account emails are not affected.
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {catalog.channels.map((c) => (
            <FormControlLabel
              key={c}
              control={<Switch checked={policy.channels[c].enabled} onChange={(e) => setMaster(c, e.target.checked)} />}
              label={catalog.channelLabels[c]}
            />
          ))}
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <FormControlLabel
          control={<Switch checked={policy.customerTextsWithoutAccount} onChange={(e) => setPolicy((p) => ({ ...p, customerTextsWithoutAccount: e.target.checked }))} />}
          label="Text and email customers on jobs entered by dispatch or a partner (no app account to opt in with)"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          App and website customers only get texts and emails they switched on themselves. Any number that replies STOP is never texted again (see the STOP list).
          {data.optOutCount ? ` ${data.optOutCount} number${data.optOutCount === 1 ? '' : 's'} on the STOP list.` : ''}
        </Typography>
      </Paper>

      {catalog.groups.filter((g) => g.key !== 'manual').map((group) => {
        const events = catalog.events.filter((e) => e.group === group.key);
        return (
          <Paper key={group.key} sx={{ p: 2 }}>
            <Typography variant="h6">{group.label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{group.blurb}</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 70 }}>On</TableCell>
                    <TableCell>Message</TableCell>
                    {catalog.channels.map((c) => <TableCell key={c} align="center" sx={{ width: 90 }}>{catalog.channelLabels[c]}</TableCell>)}
                    {events.some((e) => e.roles) && <TableCell sx={{ width: 330 }}>Who may receive it</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((e) => {
                    const ev = policy.events[e.key];
                    return (
                      <TableRow key={e.key} sx={{ opacity: ev.enabled ? 1 : 0.55 }}>
                        <TableCell>
                          <Switch size="small" checked={ev.enabled} onChange={(x) => setEvent(e.key, { enabled: x.target.checked })} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {e.label}
                            {e.critical && <Tooltip title="Switching this off loses work"><WarningAmberIcon fontSize="inherit" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }} /></Tooltip>}
                          </Typography>
                          {e.description && <Typography variant="caption" color="text.secondary">{e.description}</Typography>}
                        </TableCell>
                        {catalog.channels.map((c) => (
                          <TableCell key={c} align="center">
                            {e.channels.includes(c) ? (
                              <Tooltip title={!policy.channels[c].enabled ? `${catalog.channelLabels[c]} is off in the master switches` : ''}>
                                <span>
                                  <Checkbox
                                    size="small"
                                    checked={!!ev.channels[c]}
                                    disabled={!ev.enabled || !policy.channels[c].enabled}
                                    onChange={(x) => setEventChannel(e.key, c, x.target.checked)}
                                  />
                                </span>
                              </Tooltip>
                            ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                          </TableCell>
                        ))}
                        {events.some((x) => x.roles) && (
                          <TableCell>
                            {e.roles ? (
                              <Stack direction="row" spacing={1}>
                                <RoleFilter label="Roles" value={ev.roles} options={catalog.roleOptions} onChange={(roles) => setEvent(e.key, { roles })} />
                                <RoleFilter label="Company" value={ev.orgLevels} options={catalog.orgOptions} onChange={(orgLevels) => setEvent(e.key, { orgLevels: orgLevels.map(Number) })} />
                              </Stack>
                            ) : null}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}

      <Box sx={{ position: 'sticky', bottom: 0, py: 1.5, bgcolor: 'background.default', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button disabled={!dirty || saving} onClick={() => { setPolicy(JSON.parse(saved)); setNotice(''); }}>Discard changes</Button>
        <Button variant="contained" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save switches'}</Button>
      </Box>
    </Stack>
  );
}

/* ------------------------------------------------------------------------ */
/* Compose                                                                   */
/* ------------------------------------------------------------------------ */

const STAFF_CHANNELS = [
  { key: 'inApp', label: 'Portal bell', hint: 'Shows under the bell icon in the portal.' },
  { key: 'push', label: 'Phone push', hint: 'Driver app and browser notifications.' },
  { key: 'email', label: 'Email' }
];
const CUSTOMER_CHANNELS = [
  { key: 'push', label: 'Phone push', hint: 'Shows in the phone\'s notification bar for customers with the app.' },
  { key: 'email', label: 'Email', hint: 'Only customers who switched email alerts on.' }
];

function Compose() {
  const [catalogInfo, setCatalogInfo] = useState(null);
  const [audience, setAudience] = useState('staff');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [channels, setChannels] = useState(['inApp', 'push']);
  const [mode, setMode] = useState('all');
  const [roles, setRoles] = useState([]);
  const [orgLevels, setOrgLevels] = useState([]);
  const [people, setPeople] = useState([]);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const searchTimer = useRef(null);

  useEffect(() => {
    axios.get(`${BASE}/policy`, { headers: getAuthHeader() }).then(({ data }) => setCatalogInfo(data.catalog)).catch(() => {});
  }, []);
  const loadHistory = useCallback(() => {
    axios.get(`${BASE}/sent`, { headers: getAuthHeader() }).then(({ data }) => setHistory(data.sent || [])).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const switchAudience = (next) => {
    if (!next) return;
    setAudience(next);
    setChannels(next === 'staff' ? ['inApp', 'push'] : ['push']);
    setMode('all');
    setPeople([]);
    setOptions([]);
  };

  const search = (q) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${BASE}/recipients`, { params: { audience, q }, headers: getAuthHeader() });
        setOptions(data.recipients || []);
      } catch { setOptions([]); } finally { setSearching(false); }
    }, 250);
  };

  const channelList = audience === 'staff' ? STAFF_CHANNELS : CUSTOMER_CHANNELS;
  const toggleChannel = (key) => setChannels((c) => (c.includes(key) ? c.filter((k) => k !== key) : [...c, key]));
  const canSend = title.trim() && channels.length > 0 && (mode !== 'users' || people.length > 0);

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const { data } = await axios.post(`${BASE}/send`, {
        audience, title: title.trim(), body: body.trim(), url: url.trim(), channels,
        recipients: { mode, roles, orgLevels, ids: people.map((p) => p.id) }
      }, { headers: getAuthHeader() });
      setResult(data.sent);
      setConfirm(false);
      setTitle(''); setBody(''); setUrl(''); setPeople([]);
      loadHistory();
    } catch (e) {
      setError(errorText(e, 'Could not send'));
      setConfirm(false);
    } finally {
      setSending(false);
    }
  };

  const describeRecipients = () => {
    if (mode === 'users') return `${people.length} ${audience === 'staff' ? 'people' : 'customers'} you picked`;
    if (mode === 'roles') {
      const r = roles.map((k) => catalogInfo?.roleOptions.find((o) => o.key === k)?.label || k);
      const o = orgLevels.map((k) => catalogInfo?.orgOptions.find((x) => String(x.key) === String(k))?.label || k);
      return [r.length ? r.join(', ') : 'every role', o.length ? o.join(', ') : 'both companies'].join(' · ');
    }
    return audience === 'staff' ? 'every active portal user' : 'every customer reachable on the channels chosen';
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {result && (
        <Alert severity="success" onClose={() => setResult(null)}>
          Sent to {result.counts.recipients} {result.audience === 'staff' ? 'people' : 'customers'}:
          {result.counts.inApp ? ` ${result.counts.inApp} bell,` : ''}{result.counts.push ? ` ${result.counts.push} push,` : ''}{result.counts.email ? ` ${result.counts.email} email` : ''}
          {!result.counts.inApp && !result.counts.push && !result.counts.email ? ' nothing delivered — check the master switches and that they have the app or push turned on.' : ''}
        </Alert>
      )}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Send a notification</Typography>
        <ToggleButtonGroup exclusive value={audience} onChange={(e, v) => switchAudience(v)} size="small" sx={{ mb: 2 }}>
          <ToggleButton value="staff">Staff and providers</ToggleButton>
          <ToggleButton value="customers">Customers</ToggleButton>
        </ToggleButtonGroup>
        <Stack spacing={2}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 120 }} fullWidth size="small" />
          <TextField label="Message" value={body} onChange={(e) => setBody(e.target.value)} inputProps={{ maxLength: 1000 }} fullWidth multiline minRows={3} size="small" />
          <TextField label="Link (optional)" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={audience === 'staff' ? '/dashboard or https://…' : 'https://…'} fullWidth size="small"
            helperText={audience === 'staff' ? 'Opens when the bell entry or push is tapped. Portal paths like /dashboard work.' : 'Opens when the push is tapped.'} />
          <Box>
            <Typography variant="subtitle2">Send as</Typography>
            <Stack direction="row" flexWrap="wrap">
              {channelList.map((c) => (
                <Tooltip key={c.key} title={c.hint || ''}>
                  <FormControlLabel control={<Checkbox checked={channels.includes(c.key)} onChange={() => toggleChannel(c.key)} />} label={c.label} />
                </Tooltip>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="subtitle2">To</Typography>
            <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
              <FormControlLabel value="all" control={<Radio size="small" />} label="Everyone" />
              {audience === 'staff' && <FormControlLabel value="roles" control={<Radio size="small" />} label="By role" />}
              <FormControlLabel value="users" control={<Radio size="small" />} label={audience === 'staff' ? 'Pick people' : 'Pick customers'} />
            </RadioGroup>
            {mode === 'roles' && catalogInfo && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <RoleFilter label="Roles" value={roles} options={catalogInfo.roleOptions} onChange={setRoles} />
                <RoleFilter label="Company" value={orgLevels} options={catalogInfo.orgOptions} onChange={(v) => setOrgLevels(v.map(Number))} />
              </Stack>
            )}
            {mode === 'users' && (
              <Autocomplete
                multiple
                sx={{ mt: 1 }}
                options={options}
                value={people}
                loading={searching}
                filterOptions={(x) => x}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                getOptionLabel={(o) => o.name || ''}
                onInputChange={(e, v, reason) => { if (reason === 'input') search(v); }}
                onOpen={() => { if (!options.length) search(''); }}
                onChange={(e, v) => setPeople(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <ListItemText
                      primary={o.name}
                      secondary={[o.email || o.phone, o.company, ...(o.roles || [])].filter(Boolean).join(' · ') + (o.hasPush ? '' : ' · no phone push')}
                    />
                  </li>
                )}
                renderInput={(params) => <TextField {...params} size="small" label={audience === 'staff' ? 'Search people' : 'Search customers'} placeholder="Name, email or phone" />}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" disabled={!canSend || sending} onClick={() => setConfirm(true)}>Send…</Button>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Sent</Typography>
        {history.length === 0 ? <Typography variant="body2" color="text.secondary">Nothing sent by hand yet.</Typography> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>When</TableCell><TableCell>To</TableCell><TableCell>Title</TableCell><TableCell>Delivered</TableCell><TableCell>By</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h._id}>
                    <TableCell>{fmtDate(h.createdAt)}</TableCell>
                    <TableCell>{h.audience === 'staff' ? 'Staff' : 'Customers'} · {h.recipients?.mode === 'users' ? `${h.recipients.ids?.length || 0} picked` : h.recipients?.mode === 'roles' ? 'by role' : 'everyone'} ({h.counts?.recipients || 0})</TableCell>
                    <TableCell>{h.title}</TableCell>
                    <TableCell>
                      {h.counts?.inApp ? <Chip size="small" label={`${h.counts.inApp} bell`} sx={{ mr: 0.5 }} /> : null}
                      {h.counts?.push ? <Chip size="small" label={`${h.counts.push} push`} sx={{ mr: 0.5 }} /> : null}
                      {h.counts?.email ? <Chip size="small" label={`${h.counts.email} email`} /> : null}
                    </TableCell>
                    <TableCell>{h.sentByName || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={confirm} onClose={() => !sending && setConfirm(false)}>
        <DialogTitle>Send this now?</DialogTitle>
        <DialogContent>
          <Typography variant="body2"><b>{title}</b></Typography>
          {body && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{body}</Typography>}
          <Typography variant="body2" sx={{ mt: 2 }}>To: {describeRecipients()}</Typography>
          <Typography variant="body2">As: {channels.map((c) => channelList.find((x) => x.key === c)?.label).join(', ')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={sending}>Cancel</Button>
          <Button variant="contained" onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send'}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/* ------------------------------------------------------------------------ */
/* STOP list                                                                 */
/* ------------------------------------------------------------------------ */

function StopList() {
  const { name: orgName } = useOrg();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/opt-outs`, { headers: getAuthHeader() });
      setRows(data.optOuts || []);
    } catch (e) { setError(errorText(e, 'Could not load the STOP list')); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setError('');
    try {
      await axios.post(`${BASE}/opt-outs`, { phone, note }, { headers: getAuthHeader() });
      setPhone(''); setNote('');
      load();
    } catch (e) { setError(errorText(e, 'Could not add the number')); }
  };
  const remove = async (p) => {
    setError('');
    try {
      await axios.delete(`${BASE}/opt-outs/${encodeURIComponent(p)}`, { headers: getAuthHeader() });
      load();
    } catch (e) { setError(errorText(e, 'Could not remove the number')); }
  };

  const sourceLabel = { stop: 'Replied STOP', owner: 'Added here', carrier: 'Carrier' };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Numbers that opted out of texts</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Anyone who texts STOP to the {orgName} number lands here automatically and is never texted again (Telnyx blocks them on its side too). Texting START removes them. You can add a number by hand for someone who asked by phone.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField size="small" label="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <TextField size="small" label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={add} disabled={!phone.trim()}>Add to STOP list</Button>
        </Stack>
        {loading ? <CircularProgress size={24} /> : rows.length === 0 ? <Typography variant="body2" color="text.secondary">No numbers yet.</Typography> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Number</TableCell><TableCell>How</TableCell><TableCell>When</TableCell><TableCell>Note</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.phone}>
                    <TableCell>{r.phone}</TableCell>
                    <TableCell>{sourceLabel[r.source] || r.source}{r.keyword ? ` (${r.keyword})` : ''}</TableCell>
                    <TableCell>{fmtDate(r.optedOutAt)}</TableCell>
                    <TableCell>{r.note || ''}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove — only if the person asked to get texts again">
                        <IconButton size="small" onClick={() => remove(r.phone)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
}

/** Owner Console → Notifications. `view` is switchboard, compose or stoplist. */
export default function Notifications({ view = 'switchboard' }) {
  const content = useMemo(() => {
    if (view === 'compose') return <Compose />;
    if (view === 'stoplist') return <StopList />;
    return <Switchboard />;
  }, [view]);
  return content;
}
